import React from 'react';
import querystring from 'querystring';
import { FormattedMessage, injectIntl } from 'react-intl';
import { flowRight } from 'lodash';
import withAutoToTop from '@/HOC/withAutoToTop';
import CodeInput from '@/components/CodeInput';
import NameInput from '@/components/NameInput';
import { checkCodeStatus, NAME_MAX_LEN } from '@/constant/common';
import WelcomeHeader from '@/components/WelcomeHeader';
import loadingIcon from '@/assets/loading.svg';
import request from '@/service/request';
import { checkIsOneScreen, isAllowString } from '@/service/util';

import './index.scss';

const WAIT_TIME = 1000; // 成功跳转前等待时间

class Entrance extends React.Component {
  state = {
    codeInputStatus: 'normal',
    code: '',
    isCoding: true, // 是否在输入代码界面
    namePosting: false,
    nameInputTips: '',
    showNameInputTips: false,
    loading: false, // 数据获取中
  };

  componentWillMount() {
    const {
      location: { search },
    } = this.props;

    try {
      const query = querystring.parse(search.split('?')[1]);
      if (!query) return;

      const { classId, name } = query;

      if (classId) {
        sessionStorage.setItem('CLASSID', classId);
        this.classId = classId;

        // 存在昵称，直接加入课堂
        if (name) {
          const decodeName = decodeURIComponent(name);
          sessionStorage.setItem('NAME', decodeName);
          this.setState({
            loading: true,
          });
          this.joinClass({
            classesId: classId,
            studentName: decodeName,
          });
        } else {
          this.goInputName();
        }
      }
    } catch (err) {
      console.log(err);
    }
  }

  goInputName = () => {
    this.setState({
      isCoding: false,
    });
    setTimeout(() => {
      document.body.scrollTop = document.documentElement.scrollTop = 0;
    });
  };

  /**
   * 加入课堂
   *
   * @param {*} postData {classesId,studentName}
   */
  joinClass = async postData => {
    const { history } = this.props;

    const joinRes = await request({
      method: 'post',
      url: '/join',
      data: postData,
    });

    sessionStorage.setItem('CODE', joinRes.data);
    sessionStorage.setItem('NAME', postData.studentName);
    setTimeout(() => {
      history.replace('/answer');
    });
  };

  handleCodePost = value => {
    const { history } = this.props;
    const code = value.join('');

    this.setState({
      codeInputStatus: checkCodeStatus.LOADING,
    });

    request({
      method: 'post',
      url: '/checkCode',
      data: {
        code,
      },
    })
      .then(res => {
        const { classId, name } = res.data;

        sessionStorage.setItem('CODE', code);
        sessionStorage.setItem('CLASSID', classId);
        this.classId = classId;
        this.setState({ codeInputStatus: checkCodeStatus.SUCCESS });

        if (name) {
          // 有输入过昵称，直接跳转到答题页
          sessionStorage.setItem('NAME', name);
          setTimeout(() => history.replace('/answer'));
        } else {
          // 等待 WAIT_TIME 毫秒后跳转到输入名字
          setTimeout(() => {
            this.goInputName();
            this.setState({ code });
          }, WAIT_TIME);
        }
      })
      .catch(() => {
        this.setState({
          codeInputStatus: checkCodeStatus.FAILURE,
        });
      });
  };

  handleNamePost = async value => {
    const { namePosting } = this.state;
    const rValue = value && value.trim();

    if (namePosting || !rValue) return;
    this.setState({
      namePosting: true,
    });

    const postData = {
      classesId: this.classId,
      studentName: rValue,
    };

    try {
      const checkRes = await request({
        method: 'post',
        url: '/check',
        data: postData,
      });

      if (checkRes.data) {
        this.setState({
          showNameInputTips: true,
          nameInputTips: <FormattedMessage id="form.sameNameExist" />,
        });
      } else {
        await this.joinClass(postData);
      }
    } catch (err) {
      this.setState({
        showNameInputTips: true,
        nameInputTips: <FormattedMessage id="form.submitFail" />,
      });
    } finally {
      this.setState({
        namePosting: false,
      });
    }
  };

  handleNameChange = (val = '') => {
    const name = val.trim();

    // 检查特殊字符
    if (!isAllowString(name)) {
      this.setState({
        showNameInputTips: true,
        nameInputTips: <FormattedMessage id="form.notAllow" />,
      });
      return;
    }

    const moreThanLen = name.length > NAME_MAX_LEN;

    this.setState({
      showNameInputTips: moreThanLen,
      nameInputTips: moreThanLen ? (
        <FormattedMessage
          id="form.nameMaxLenTips"
          values={{ maxLen: NAME_MAX_LEN }}
        />
      ) : (
        ''
      ),
    });
  };

  render() {
    const { intl } = this.props;
    const {
      codeInputStatus,
      code,
      isCoding,
      namePosting,
      showNameInputTips,
      nameInputTips,
      loading,
    } = this.state;
    const isOneScreen = checkIsOneScreen()
    const title = isCoding ? (
      <FormattedMessage id="form.codeLabel" />
    ) : (
      <FormattedMessage id="form.nameLabel" />
    );

    return (
      <div className="entrance-page">
        <div
          className="entrance-loading-mask"
          style={loading ? {} : { display: 'none' }}
        >
          <div className="entrance-loading-container">
            <img className="loading-icon" src={loadingIcon} alt="" />
            <div className="loading-label">
              <FormattedMessage id="app.pageLoading" />
            </div>
          </div>
        </div>
        <div className={`entrance-content ${isOneScreen ? 'onescreen' : 'airclass'}`}>
          <WelcomeHeader name={code} />
          <div className="entrance-body">
            <div className="entrance-tips">{title}</div>
            {isCoding ? (
              <CodeInput
                status={codeInputStatus}
                onOk={this.handleCodePost}
                successMsg={<FormattedMessage id="form.correctCode" />}
                failMsg={<FormattedMessage id="form.incorrectCode" />}
              />
            ) : (
              <NameInput
                showTips={showNameInputTips}
                tips={nameInputTips}
                onOk={this.handleNamePost}
                onChange={this.handleNameChange}
                loading={namePosting}
                placeHolder={intl.formatMessage(
                  { id: 'form.nameMaxLenTips' },
                  { maxLen: NAME_MAX_LEN }
                )}
              />
            )}
          </div>
          {isOneScreen ? <div className="footer-bg-new" /> : <div className="footer-img" />}
        </div>
        {!isOneScreen && <div className="footer-bg" />}
      </div>
    );
  }
}

export default flowRight([withAutoToTop, injectIntl])(Entrance);
